/**
 * AIProgressIndicator — a lightweight visual for long-running AI calls.
 *
 * Why: LLM calls in this app take anywhere from ~8s (Claude label copy)
 * to ~45s (Gemini + googleSearch recipe estimate). Users on slow links
 * were assuming the button was broken. This component makes the wait
 * legible — an animated bar shows fake linear progress against a typical
 * duration, an elapsed-seconds counter runs live, and the status caption
 * rotates through short waypoints so it never looks frozen.
 *
 * Usage:
 *   <AIProgressIndicator
 *     active={loading}
 *     estimatedMs={20000}
 *     steps={[
 *       { at: 0,    label: 'Warming up the model…' },
 *       { at: 4000, label: 'Consulting FDA references…' },
 *       { at: 12000,label: 'Compiling nutrition panel…' },
 *     ]}
 *   />
 *
 * Notes:
 *  - Bar caps at 95% until `active` flips false; then it snaps to 100%
 *    and fades out shortly after, so users see a "done" state.
 *  - No external state — this is a self-contained indicator.
 *  - Zero deps beyond React + Tailwind + lucide-react.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';

const DEFAULT_STEPS = [
  { at: 0,     label: 'Warming up the model…' },
  { at: 3000,  label: 'Thinking…' },
  { at: 10000, label: 'Compiling response…' },
  { at: 25000, label: 'Almost done…' },
];

export default function AIProgressIndicator({
  active,
  estimatedMs = 15000,
  steps = DEFAULT_STEPS,
  label = 'AI is working',
  className = '',
  ['data-testid']: testid = 'ai-progress',
}) {
  const [elapsed, setElapsed]     = useState(0);
  const [done, setDone]           = useState(false);
  const startRef  = useRef(null);
  const timerRef  = useRef(null);
  const hideRef   = useRef(null);

  useEffect(() => {
    if (active) {
      setDone(false);
      startRef.current = Date.now();
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed(Date.now() - startRef.current);
      }, 250);
      if (hideRef.current) { clearTimeout(hideRef.current); hideRef.current = null; }
    } else if (startRef.current) {
      // Snap to 100% and keep the bar visible briefly so it doesn't
      // just yank away — users read that as "actually finished".
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      setDone(true);
      hideRef.current = setTimeout(() => {
        setDone(false);
        startRef.current = null;
        setElapsed(0);
      }, 900);
    }
    return () => {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    };
  }, [active]);

  if (!active && !done) return null;

  // Linear progress against the estimate, capped at 95% until done.
  const rawPct  = Math.min(95, (elapsed / estimatedMs) * 100);
  const pct     = done ? 100 : rawPct;
  const secs    = (elapsed / 1000).toFixed(1);

  // Pick the latest step whose `at` threshold has passed.
  const currentStep = [...steps]
    .sort((a, b) => a.at - b.at)
    .filter((s) => s.at <= elapsed)
    .pop() || steps[0];
  const caption = done ? 'Done — applying results…' : (currentStep?.label || label);

  return (
    <div
      className={`rounded-md border border-primary/30 bg-primary/5 p-3 ${className}`}
      role="status"
      aria-live="polite"
      data-testid={testid}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-primary">
          {done
            ? <Sparkles className="w-4 h-4" />
            : <Loader2 className="w-4 h-4 animate-spin" />}
          <span data-testid={`${testid}-caption`}>{caption}</span>
        </div>
        <div className="text-xs text-muted-foreground tabular-nums">
          {secs}s
        </div>
      </div>
      <div className="h-1.5 w-full rounded-full bg-primary/10 overflow-hidden">
        <div
          className="h-full bg-primary transition-all duration-300 ease-out"
          style={{ width: `${pct}%` }}
          data-testid={`${testid}-bar`}
        />
      </div>
    </div>
  );
}
